import type { LegalEntity } from '@shared/types';

export const MOBILE_REQUISITES_ENTITIES = [
  'DOKTOR_SERVER',
  'DOKTOR_TRADE',
] as const satisfies readonly LegalEntity[];

export type MobileRequisitesLegalEntity = (typeof MOBILE_REQUISITES_ENTITIES)[number];

export interface DatedCompanyRequisitesRow {
  legalEntity: LegalEntity;
  effectiveFrom: string;
}

export interface MobileCompanyRequisitesGroup<T extends DatedCompanyRequisitesRow> {
  legalEntity: MobileRequisitesLegalEntity;
  rows: T[];
}

function twelveMonthsAgoUtcDate(now: Date): number {
  const year = now.getUTCFullYear() - 1;
  const month = now.getUTCMonth();
  const day = now.getUTCDate();
  const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

  return Date.UTC(year, month, Math.min(day, lastDayOfMonth));
}

function toUtcBusinessDate(value: string): number {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return Number.NaN;

  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Компактная мобильная история: для каждого ООО показываем вступившие версии
 * за последние 12 месяцев, а при отсутствии изменений — одну актуальную.
 * API и desktop при этом продолжают получать полную историю, включая ИП.
 */
export function selectMobileCompanyRequisites<T extends DatedCompanyRequisitesRow>(
  rows: readonly T[],
  now: Date = new Date(),
): MobileCompanyRequisitesGroup<T>[] {
  const todayTimestamp = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  const cutoffTimestamp = twelveMonthsAgoUtcDate(now);

  return MOBILE_REQUISITES_ENTITIES.flatMap((legalEntity) => {
    const effectiveRows = rows
      .filter((row) => row.legalEntity === legalEntity)
      .map((row) => ({ row, timestamp: toUtcBusinessDate(row.effectiveFrom) }))
      .filter(({ timestamp }) => Number.isFinite(timestamp) && timestamp <= todayTimestamp)
      .sort((left, right) => right.timestamp - left.timestamp);

    const recentRows = effectiveRows
      .filter(({ timestamp }) => timestamp >= cutoffTimestamp)
      .map(({ row }) => row);
    const visibleRows = recentRows.length > 0
      ? recentRows
      : effectiveRows.slice(0, 1).map(({ row }) => row);

    return visibleRows.length > 0 ? [{ legalEntity, rows: visibleRows }] : [];
  });
}
